import React, { useCallback, useEffect, useMemo, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@mui/material";
import { DataExtractionModal } from "components/Data-extraction/DataExtractionModal";
import { BulkActionsBar } from "components/Deal-workspace/Workspace-metadata-components/BulkActionsBar";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useActionHook, useModals, usePrompt } from "hooks";
import { IApiServiceError, useBulkUploadActions, useDealActions, usePickUserPermissions } from "store";
import { ApiValidationErrors, IBulkUpdateFilesRepoData, IUpdateFileTagData, UserPermissionType } from "store/types";
import { toastify } from "utils/tyostify";

import { FileType } from "../../../constants";
import { ClassificationPrompt } from "../../File-repository/Table/ClassificationPrompt";
import { CurrencyPrompt } from "../../File-repository/Table/CurrencyPrompt";
import { EffectiveDatePrompt } from "../../File-repository/Table/EffectiveDatePrompt";
import { InvestorPrompt } from "../../File-repository/Table/InvestorPrompt";
import { useBulkReviewState } from "./hooks/useBulkReviewState";
import { BulkReviewLongTable } from "./table/BulkReviewLongTable";
import { BulkReviewWideTable } from "./table/BulkReviewWideTable";

type ReviewView = "long" | "wide";

interface Props {
  batchId: string;
  onCancel: () => void;
  onSubmit: (result: { saved: boolean }) => void;
}

const SummaryCard: React.FC<{ label: string; value: number; color?: string }> = ({ label, value, color }) => (
  <Stack
    sx={{ flex: 1, boxShadow: "0 1px 2px 0 #1212171a", borderRadius: "8px", px: "20px", py: "16px", bgcolor: "white" }}
    gap="4px"
  >
    <Typography sx={{ fontSize: 14, color: "#496682" }}>{label}</Typography>
    <Typography sx={{ fontSize: 24, fontWeight: 600, color: color ?? "#071f35" }}>{value}</Typography>
  </Stack>
);

export const BulkUploadReview: React.FC<Props> = ({ batchId, onCancel, onSubmit }) => {
  const { bulkUpdateFilesRepoAction, downloadFilesRepoAction, updateFileTagAction } = useDealActions();
  const { removeBulkBatchAction, saveBulkUploadBatchAction, dismissBulkUploadBatchAction } = useBulkUploadActions();
  const { errorModal } = useModals();
  const openPrompt = usePrompt();
  const { loading, review, rows, valueRows, measureColumns, readOnly, updateFileRow, refetch } =
    useBulkReviewState(batchId);

  const [view, setView] = useState<ReviewView>("wide");
  const [saveErrors, setSaveErrors] = useState<ApiValidationErrors>({});

  useEffect(() => {
    setSaveErrors({});
  }, [review]);

  const { correctCount, conflictCount } = useMemo(() => {
    const conflict = valueRows.filter((row) => !row.isComplete || Boolean(saveErrors[row.tagId])).length;
    return { correctCount: valueRows.length - conflict, conflictCount: conflict };
  }, [valueRows, saveErrors]);

  const editTag = useCallback(
    async (fileId: number, tagId: number, data: IUpdateFileTagData) => {
      try {
        await updateFileTagAction(fileId, tagId, data);
        refetch();
      } catch (error) {
        const apiError = error as IApiServiceError;
        const messages =
          apiError?.errors && Object.keys(apiError.errors).length
            ? (Object.values(apiError.errors)
                .flat(Infinity)
                .filter((m) => typeof m === "string") as string[])
            : [apiError?.message || "Failed to update the value."];
        setSaveErrors((prev) => ({ ...prev, [tagId]: messages }));
        toastify({ message: "Failed to update the value. Please try again.", type: "error" });
      }
    },
    [updateFileTagAction, refetch]
  );

  const dealMetricsPermission = usePickUserPermissions("deal_metrics");
  const canSave = !readOnly && dealMetricsPermission !== UserPermissionType.NO_ACCESS;

  const { loading: saving, action: save } = useActionHook(saveBulkUploadBatchAction, {
    onSuccess: (res) => {
      setSaveErrors({});
      toastify({
        message: `Batch saved — ${res.saved_count} value${res.saved_count === 1 ? "" : "s"} imported.`,
        type: "success"
      });
      removeBulkBatchAction(batchId);
      onSubmit?.({ saved: true });
    },
    onReject: (error: IApiServiceError) => {
      if (error.errors && Object.keys(error.errors).length) {
        setSaveErrors(error.errors);
        setView("long");
      }
      toastify({
        message: "Failed to save the batch. Please check the values and try again.",
        type: "error"
      });
    }
  });

  const { loading: dismissing, action: dismiss } = useActionHook(dismissBulkUploadBatchAction, {
    onSuccess: () => {
      removeBulkBatchAction(batchId);
      onCancel?.();
    },
    onReject: (error: IApiServiceError) => {
      const message =
        error.message === "batch_not_terminal"
          ? "Some files are still processing. Cancel or wait before dismissing."
          : error.message || "Failed to dismiss the batch.";
      toastify({ message, type: "warn" });
    }
  });

  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const toggleSelect = useCallback(
    (fileId: number) =>
      setSelectedIds((prev) => (prev.includes(fileId) ? prev.filter((id) => id !== fileId) : [...prev, fileId])),
    []
  );
  const clearSelection = useCallback(() => setSelectedIds([]), []);

  const selectedFiles = useMemo(() => rows.filter((row) => selectedIds.includes(row.fileId)), [rows, selectedIds]);
  const selectedItems = useMemo(
    () =>
      selectedFiles.map((row) => ({
        id: row.fileId,
        entity_type: "file" as const,
        type: row.fileType as FileType,
        name: row.fileName
      })),
    [selectedFiles]
  );

  const runBulkUpdate = useCallback(
    async (patch: Omit<IBulkUpdateFilesRepoData, "file_ids">) => {
      if (!selectedIds.length) return;
      await bulkUpdateFilesRepoAction({ file_ids: selectedIds, ...patch });
      refetch();
    },
    [selectedIds, bulkUpdateFilesRepoAction, refetch]
  );

  const popoverAt = (anchorEl: HTMLElement | null) =>
    anchorEl ? { anchorEl } : { anchorPosition: { top: window.innerHeight / 2, left: window.innerWidth / 2 } };

  const handleChangeClassification = useCallback(
    async (anchorEl: HTMLElement | null) => {
      const result = await openPrompt({
        componentType: "popover",
        ...popoverAt(anchorEl),
        Component: ClassificationPrompt,
        props: { currentClassification: null }
      });
      if (!result) return;
      runBulkUpdate({ document_type: result.classification });
    },
    [openPrompt, runBulkUpdate]
  );

  const handleChangeCurrency = useCallback(
    async (anchorEl: HTMLElement | null) => {
      const result = await openPrompt({
        componentType: "popover",
        ...popoverAt(anchorEl),
        Component: CurrencyPrompt,
        props: { currentCurrency: null }
      });
      if (!result || !result.currency) return;
      runBulkUpdate({ currency: result.currency });
    },
    [openPrompt, runBulkUpdate]
  );

  const handleChangeInvestor = useCallback(
    async (anchorEl: HTMLElement | null) => {
      const result = await openPrompt({
        componentType: "popover",
        ...popoverAt(anchorEl),
        Component: InvestorPrompt,
        props: {
          currentInvestorId: null,
          currentAllInvestors: false,
          currentSplitByCommitment: false,
          dealId: null,
          showGrouped: false
        }
      });
      if (!result) return;
      runBulkUpdate({
        investor_id: result.investor?.id ?? null,
        all_investors: result.allInvestors,
        split_by_commitment: result.allInvestors ? true : result.splitByCommitment,
        investor_ids: result.investorIds,
        group_commitment: result.groupCommitment,
        entire_commitment: result.entireCommitment
      });
    },
    [openPrompt, runBulkUpdate]
  );

  const handleChangeEffectiveDate = useCallback(
    async (anchorEl: HTMLElement | null) => {
      const result = await openPrompt({
        componentType: "popover",
        ...popoverAt(anchorEl),
        Component: EffectiveDatePrompt,
        props: { currentDate: null }
      });
      if (!result) return;
      runBulkUpdate({ effective_date: result.effectiveDate });
    },
    [openPrompt, runBulkUpdate]
  );

  const openExtractor = useCallback(
    async (initialShowParameters?: boolean) => {
      const pdfFiles = selectedFiles.filter((f) => f.fileType === FileType.PDF);
      if (!pdfFiles.length) return;
      await openPrompt({
        componentType: "fullscreen",
        Component: DataExtractionModal,
        props: {
          fileIds: pdfFiles.map((f) => f.fileId),
          fileNames: pdfFiles.reduce<Record<number, string>>((acc, f) => ({ ...acc, [f.fileId]: f.fileName }), {}),
          source: "deal" as const,
          ...(initialShowParameters !== undefined && { initialShowParameters })
        }
      });
      refetch();
    },
    [selectedFiles, openPrompt, refetch]
  );

  const handleDownload = useCallback(async () => {
    if (!selectedIds.length) return;
    await downloadFilesRepoAction(selectedIds).catch(() => errorModal({ msg: "Failed to download files." }));
  }, [selectedIds, downloadFilesRepoAction, errorModal]);

  return (
    <Stack sx={{ height: "100%", minHeight: 0, minWidth: 0, bgcolor: "#f6f8fc" }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        sx={{ px: "32px", pt: "24px", pb: "16px", bgcolor: "white", borderBottom: "1px solid #dbe8f5" }}
      >
        <Typography sx={{ fontSize: 20, fontWeight: 600, color: "#071f35" }}>Uploaded Files</Typography>
        <IconButton onClick={onCancel}>
          <CloseIcon sx={{ color: "#496682" }} />
        </IconButton>
      </Stack>

      <Stack direction="row" gap="16px" sx={{ px: "32px", py: "16px" }}>
        <SummaryCard label="All Files" value={review?.file_count ?? 0} />
        <SummaryCard label="Correct Values" value={correctCount} color="#1b7a4a" />
        <SummaryCard label="Conflict" value={conflictCount} color="#d32f2f" />
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, display: "flex", px: "32px", pb: "16px" }}>
        {loading && !review ? (
          <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
            <CircularProgress />
          </Stack>
        ) : (
          <Box
            sx={{
              bgcolor: "white",
              borderRadius: "8px",
              p: "8px",
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              overflow: "hidden"
            }}
          >
            <Stack direction="row" justifyContent="flex-end">
              <ToggleButtonGroup
                exclusive
                size="small"
                value={view}
                onChange={(_, next: ReviewView | null) => next && setView(next)}
                sx={{
                  height: "32px",
                  border: "1px solid #dbe8f5",
                  borderRadius: "999px",
                  p: "4px",
                  gap: "4px",
                  "& .MuiToggleButtonGroup-grouped": {
                    m: 0,
                    border: "none",
                    borderRadius: "999px !important",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "14px",
                    color: "#496682",
                    px: "24px",
                    py: "6px",
                    "&:hover": { bgcolor: "rgba(7, 31, 53, 0.04)" },
                    "&.Mui-selected": {
                      bgcolor: "#071f35",
                      color: "#fff",
                      "&:hover": { bgcolor: "#0a2a45" }
                    }
                  }
                }}
              >
                <ToggleButton value="long">Long</ToggleButton>
                <ToggleButton value="wide">Wide</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            {view === "long" ? (
              <BulkReviewLongTable
                rows={valueRows}
                onExtractorClose={refetch}
                errors={saveErrors}
                readOnly={readOnly}
                onEditTag={editTag}
              />
            ) : (
              <BulkReviewWideTable
                rows={rows}
                measureColumns={measureColumns}
                readOnly={readOnly}
                onUpdateFile={updateFileRow}
                onRefetch={refetch}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
              />
            )}
          </Box>
        )}
      </Box>

      {!readOnly && (
        <Stack
          direction="row"
          justifyContent="flex-end"
          gap="12px"
          sx={{ px: "32px", py: "16px", bgcolor: "white", borderTop: "1px solid #dbe8f5" }}
        >
          <Button
            variant="outlined"
            disabled={dismissing}
            onClick={() => dismiss(batchId)}
            sx={{ borderRadius: "32px", borderColor: "#7b92a9", color: "#071f35", textTransform: "none", px: "20px" }}
          >
            Decline all changes
          </Button>
          <MaterialTooltip
            placement="top"
            variant="dark"
            title={
              !canSave
                ? "You don't have permission to save metrics."
                : conflictCount
                  ? "Resolve all conflicts before saving."
                  : ""
            }
            isShow={!canSave || Boolean(conflictCount)}
          >
            <span>
              <Button
                variant="contained"
                disabled={!canSave || saving || Boolean(conflictCount)}
                onClick={() => save(batchId)}
                sx={{
                  borderRadius: "32px",
                  bgcolor: "#071f35",
                  textTransform: "none",
                  px: "20px",
                  "&:hover": { bgcolor: "#0a2a45" }
                }}
              >
                {saving ? "Saving..." : "Apply & Save"}
              </Button>
            </span>
          </MaterialTooltip>
        </Stack>
      )}

      {!readOnly && view === "wide" && selectedItems.length > 0 && (
        <BulkActionsBar
          selectedItems={selectedItems}
          onClose={clearSelection}
          onView={() => openExtractor(false)}
          onExtract={() => openExtractor()}
          onDownload={handleDownload}
          onChangeClassification={handleChangeClassification}
          onChangeCurrency={handleChangeCurrency}
          onChangeInvestor={handleChangeInvestor}
          onChangeEffectiveDate={handleChangeEffectiveDate}
        />
      )}
    </Stack>
  );
};
